import {computed, ref} from 'vue';
import {useProjectInformationStore} from '@common/stores';
import {uploadFiles} from '@common/utils';
import {projectInformationEndpoints} from '@registry';
import {defineStore, storeToRefs} from 'pinia';
import {useLogger, useTranslation} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {ValidateProjectRequest} from '@api';

export const useProjectValidationStore = defineStore('project-validation', () => {
    const logger = useLogger();

    const {t} = useTranslation();

    // general project information
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);
    const freelancer = computed(() => projectInformation.value?.freelancer?.fullName);

    const shouldDisplayUmbrellaCompanyAffiliationDialog = ref<boolean>();

    async function validateProject({
        projectId,
        lotId,
        projectValidationParam,
        additionalDocument,
    }: ValidateProjectRequest & {additionalDocument?: File}) {
        const requestParameters: ValidateProjectRequest = {
            projectId,
            lotId,
            projectValidationParam,
        };

        if (additionalDocument) {
            const [fileId] = await uploadFiles([additionalDocument]);
            requestParameters.projectValidationParam!.extraDocumentId = fileId;
            requestParameters.projectValidationParam!.extraDocumentName = additionalDocument.name;
        } else {
            requestParameters.projectValidationParam!.extraDocumentId = undefined;
            requestParameters.projectValidationParam!.extraDocumentName = undefined;
        }

        try {
            await projectInformationEndpoints.validateProject(requestParameters);
        } catch (e: any) {
            logger.error(e);
            if (e.statusCode === 400) displayBadRequestError(e.cause.data.errorCode);
            else if (e.statusCode === 403) shouldDisplayUmbrellaCompanyAffiliationDialog.value = true;
            else pushVJoySnackbar({props: {message: t('projectclient.projectinformation.validation.error'), level: 'error'}});
            return Promise.reject(e);
        }
    }

    function displayBadRequestError(errorCode: string) {
        switch (errorCode) {
            case 'ExternalReferenceMandatory':
                return pushVJoySnackbar({
                    props: {
                        message: t('projectclient.projectinformation.validation.error.ExternalReferenceMandatory'),
                        level: 'warning',
                        duration: 'forever',
                    },
                });
            case 'PurchaseOrderMandatory':
                return pushVJoySnackbar({
                    props: {
                        message: t('projectclient.projectinformation.validation.error.PurchaseOrderMandatory'),
                        level: 'warning',
                        duration: 'forever',
                    },
                });
            case 'FreelancerInvoicesNotAllowed':
                return pushVJoySnackbar({
                    props: {
                        message: t('projectclient.projectinformation.validation.error.FreelancerInvoicesNotAllowed', {0: freelancer.value}),
                        level: 'warning',
                        duration: 'forever',
                    },
                });
            case 'InvoiceExtraDocumentForbidden':
                return pushVJoySnackbar({
                    props: {
                        message: t('projectclient.projectinformation.validation.error.InvoiceExtraDocumentForbidden'),
                        level: 'warning',
                        duration: 'forever',
                    },
                });
            case 'ProjectPeriodRequired':
                return pushVJoySnackbar({
                    props: {
                        message: t('projectclient.projectinformation.validation.error.ProjectPeriodRequired'),
                        level: 'warning',
                        duration: 'forever',
                    },
                });
        }
    }

    function setShouldDisplayUmbrellaCompanyAffiliationDialog(newShouldDisplayUmbrellaCompanyAffiliationDialog: boolean) {
        shouldDisplayUmbrellaCompanyAffiliationDialog.value = newShouldDisplayUmbrellaCompanyAffiliationDialog;
    }

    return {
        shouldDisplayUmbrellaCompanyAffiliationDialog: readonlyState(shouldDisplayUmbrellaCompanyAffiliationDialog),
        setShouldDisplayUmbrellaCompanyAffiliationDialog,
        validateProject,
    };
});
